import { supabase } from '../lib/supabase';

export const payrollSyncMonitoringService = {
  // ============================================================================
  // SYNC STATISTICS
  // ============================================================================

  async getAllSyncStatistics() {
    const { data, error } = await supabase?.rpc('get_payroll_sync_statistics');
    
    if (error) throw error;
    
    return data?.map(row => ({
      providerName: row?.provider_name,
      integrationStatus: row?.integration_status,
      lastSyncAt: row?.last_sync_at,
      nextSyncAt: row?.next_sync_at,
      syncFrequency: row?.sync_frequency,
      isActive: row?.is_active,
      unresolvedConflicts: row?.unresolved_conflicts,
      totalConflicts: row?.total_conflicts,
      connectionHealth: row?.connection_health
    })) || [];
  },

  // ============================================================================
  // SYNC CONFLICTS
  // ============================================================================

  async getAllConflicts() {
    const { data, error } = await supabase
      ?.from('payroll_sync_conflicts')
      ?.select(`
        *,
        payroll_provider_configs (provider_name)
      `)
      ?.order('detected_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      providerConfigId: row?.provider_config_id,
      providerName: row?.payroll_provider_configs?.provider_name,
      conflictType: row?.conflict_type,
      severity: row?.severity,
      description: row?.description,
      affectedRecords: row?.affected_records,
      conflictData: row?.conflict_data,
      resolutionStatus: row?.resolution_status,
      resolvedAt: row?.resolved_at,
      resolvedBy: row?.resolved_by,
      resolutionNotes: row?.resolution_notes,
      detectedAt: row?.detected_at,
      createdAt: row?.created_at
    })) || [];
  },

  async getUnresolvedConflicts() {
    const { data, error } = await supabase
      ?.from('payroll_sync_conflicts')
      ?.select(`
        *,
        payroll_provider_configs (provider_name)
      `)
      ?.eq('resolution_status', 'unresolved')
      ?.order('detected_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      providerConfigId: row?.provider_config_id,
      providerName: row?.payroll_provider_configs?.provider_name,
      conflictType: row?.conflict_type,
      severity: row?.severity,
      description: row?.description,
      affectedRecords: row?.affected_records,
      conflictData: row?.conflict_data,
      detectedAt: row?.detected_at
    })) || [];
  },

  async getConflictsByProvider(providerConfigId) {
    const { data, error } = await supabase
      ?.from('payroll_sync_conflicts')
      ?.select('*')
      ?.eq('provider_config_id', providerConfigId)
      ?.order('detected_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      conflictType: row?.conflict_type,
      severity: row?.severity,
      description: row?.description,
      affectedRecords: row?.affected_records,
      resolutionStatus: row?.resolution_status,
      detectedAt: row?.detected_at
    })) || [];
  },

  async createConflict(conflict) {
    const { data, error } = await supabase
      ?.from('payroll_sync_conflicts')
      ?.insert({
        provider_config_id: conflict?.providerConfigId,
        conflict_type: conflict?.conflictType,
        severity: conflict?.severity,
        description: conflict?.description,
        affected_records: conflict?.affectedRecords,
        conflict_data: conflict?.conflictData,
        resolution_status: 'unresolved'
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      conflictType: data?.conflict_type,
      severity: data?.severity,
      detectedAt: data?.detected_at
    };
  },

  async resolveConflict(conflictId, resolution) {
    const { data, error } = await supabase
      ?.from('payroll_sync_conflicts')
      ?.update({
        resolution_status: 'resolved',
        resolved_at: new Date()?.toISOString(),
        resolved_by: resolution?.resolvedBy,
        resolution_notes: resolution?.resolutionNotes,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', conflictId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      resolutionStatus: data?.resolution_status,
      resolvedAt: data?.resolved_at
    };
  },

  // ============================================================================
  // REAL-TIME SUBSCRIPTIONS
  // ============================================================================

  subscribeToSyncUpdates(callback) {
    const channel = supabase
      ?.channel('payroll-sync-updates')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payroll_provider_configs'
        },
        () => {
          callback();
        }
      )
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payroll_sync_conflicts'
        },
        () => {
          callback();
        }
      )
      ?.subscribe();
    
    return channel;
  },

  unsubscribeFromSyncUpdates(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};

export default payrollSyncMonitoringService;